import type { Course } from '@rcs/data-schema';
import {
  summarize,
  type RideStatistics,
  type SimulationOptions,
  type SimulationResult,
} from '@rcs/physics';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toCourseInput } from '../course/input';
import { toSegmentSpecs } from '../course/segments';
import type { PlaybackController } from '../playback/controller';
import { createComputeClient, type ComputeClient } from '../worker/client';
import type { ComputeSuccess } from '../worker/protocol';
import { continueRide, QUICK_HORIZON } from './continue-ride';

/** 仮の結果の終わりの、この時間 [s] 手前まで再生が進んだら、先をさらに計算する。 */
const EXTEND_MARGIN = 1.5;
import { ControlTimeline } from './control-timeline';

/** 操作を反映した走行。 */
export interface ControlledRide {
  readonly result: SimulationResult;
  readonly stats: RideStatistics;
  /** 先の一部だけを計算した仮の結果か（全体の計算を Worker で待っている） */
  readonly provisional: boolean;
}

/** {@link useRideControl} の戻り値。 */
export interface RideControl {
  /** 操作を反映した走行（操作がなければ `undefined` = もとの走行のまま） */
  readonly controlled: ControlledRide | undefined;
  /** 押している（加速中）か */
  readonly holding: boolean;
  /** 記録した操作の数（押して離した分・ブースト・押している分） */
  readonly count: number;
  /** 加速ボタンを押す（再生中のみ） */
  readonly press: () => void;
  /** 加速ボタンを離す */
  readonly release: () => void;
  /** ブースト（再生中のみ） */
  readonly boost: () => void;
  /** 押している間の加速度 [m/s²] を変える（押している間なら、その時刻で区切って計算し直す） */
  readonly setAcceleration: (acceleration: number) => void;
  /** 時刻 `time` にブースト中か */
  readonly isBoosting: (time: number) => boolean;
}

/**
 * 走行中の操作（押している間の加速・ブースト）を記録し、走行を計算し直す（FR-SIM-008、ADR-0013）。
 *
 * 操作のたびに、まずメインスレッドで先の数秒だけを計算して車両の動きへすぐ反映し、
 * 同時に Worker で走行全体を計算し直して、グラフ・評価・要約を実際の走りに更新する。
 * 物理は決定的なので、どちらも同じ操作から同じ走行になり、再生位置は保たれる。
 * 最初から再生し直す（最初の操作より前へ戻る）と、操作を消して、もとの走行に戻す。
 *
 * @param ride もとの走行（操作なし）。変わると操作を消す
 * @param course `ride` を計算したコース
 * @param simulation 走行の条件（`useRide` に渡したもの）
 * @param initialAcceleration 押している間の加速度の初期値 [m/s²]
 */
export function useRideControl(
  ride: ComputeSuccess | undefined,
  course: Course,
  simulation: SimulationOptions | undefined,
  playback: PlaybackController | undefined,
  initialAcceleration: number,
): RideControl {
  const [timeline] = useState(() => new ControlTimeline());
  // 表示する状態。どの走行の操作かを持ち、走行が変わったら（編集など）、操作なしとして扱う
  const [view, setView] = useState<{
    readonly ride: ComputeSuccess | undefined;
    readonly playback: PlaybackController | undefined;
    readonly controlled: ControlledRide | undefined;
    readonly holding: boolean;
    readonly count: number;
  }>({ ride: undefined, playback: undefined, controlled: undefined, holding: false, count: 0 });
  const controlledRef = useRef<ControlledRide | undefined>(undefined);
  const versionRef = useRef(0);
  const clientRef = useRef<ComputeClient | undefined>(undefined);
  // Worker の計算は、同時に 1 つまで。計算中に操作が変わったら、終わってから最新の操作で 1 回だけ計算し直す
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const requestFullRef = useRef<() => void>(() => undefined);
  const accelerationRef = useRef(initialAcceleration);
  // コースの区間は、走行と同じ時点のものを使う（useRide と同じ条件）
  const options = useMemo<SimulationOptions>(
    () => ({ ...simulation, segments: toSegmentSpecs(course.controlPoints) }),
    [simulation, course.controlPoints],
  );

  // 走行全体を計算し直す Worker（コースの計算とは別。古い要求の結果は捨てる）
  useEffect(() => {
    return () => {
      clientRef.current?.dispose();
      clientRef.current = undefined;
    };
  }, []);

  const show = useCallback(
    (next: ControlledRide | undefined) => {
      controlledRef.current = next;
      setView({
        ride,
        playback,
        controlled: next,
        holding: timeline.isHolding,
        count: timeline.intervals.length,
      });
      if (ride && playback) playback.setDuration((next?.result ?? ride.result).meta.duration);
    },
    [ride, playback, timeline],
  );

  /** 記録した操作で、走行全体を Worker で計算し直す（計算中なら、終わってからもう一度） */
  const requestFull = useCallback(() => {
    if (!ride) return;
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;
    const version = versionRef.current;
    clientRef.current ??= createComputeClient();
    void clientRef.current
      .compute({
        course: toCourseInput(course),
        simulation: { ...options, control: timeline.intervals },
      })
      .then((outcome) => {
        inFlightRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          if (controlledRef.current?.provisional) requestFullRef.current();
          return;
        }
        if (outcome.status !== 'ok' || version !== versionRef.current) return;
        // 計算したコースが、表示中の走行と同じか（途中で編集されていないか）
        if (outcome.value.track.count !== ride.track.count) return;
        show({ result: outcome.value.result, stats: outcome.value.stats, provisional: false });
      });
  }, [ride, options, course, timeline, show]);
  useEffect(() => {
    requestFullRef.current = requestFull;
  }, [requestFull]);

  /** 記録した操作で、時刻 `time` から先を計算し直す */
  const apply = useCallback(
    (time: number) => {
      if (!ride) return;
      versionRef.current += 1;
      if (timeline.isEmpty) {
        show(undefined);
        return;
      }
      const base = controlledRef.current?.result ?? ride.result;
      const quick = continueRide(
        ride.track,
        options,
        base,
        timeline.intervals,
        time,
        QUICK_HORIZON,
      );
      show({
        result: quick.result,
        stats: summarize(quick.result),
        provisional: quick.provisional,
      });
      if (quick.provisional) requestFull();
    },
    [ride, options, timeline, show, requestFull],
  );

  /**
   * 仮の結果の終わりが近づいたら（Worker の計算を待っている間）、同じ操作のまま先をさらに計算してつなぐ。
   * 再生が仮の結果の終わりで止まらないようにする
   */
  const extend = useCallback(() => {
    const current = controlledRef.current;
    if (!ride || !playback || !current?.provisional) return;
    const end = current.result.meta.duration;
    if (playback.time < end - EXTEND_MARGIN) return;
    const quick = continueRide(
      ride.track,
      options,
      current.result,
      timeline.intervals,
      end,
      QUICK_HORIZON,
    );
    show({ result: quick.result, stats: summarize(quick.result), provisional: quick.provisional });
  }, [ride, playback, options, timeline, show]);

  // 走行が変わったら（編集・車両の変更・コースの切り替え）、操作を消す
  useEffect(() => {
    timeline.clear();
    versionRef.current += 1;
    controlledRef.current = undefined;
  }, [ride, playback, timeline]);

  // 最初から再生し直したら、操作を消す。止まったら、押していた分を離す
  useEffect(() => {
    if (!playback) return undefined;
    return playback.subscribe(() => {
      const snapshot = playback.getSnapshot();
      const first = timeline.firstStart;
      if (first !== undefined && playback.time < first - 1e-9) {
        timeline.clear();
        apply(playback.time);
        return;
      }
      // 仮の結果の終わりに追いついて止まったのは、人が止めたのではない（押したままにする）
      const waiting = snapshot.ended && controlledRef.current?.provisional === true;
      if (!snapshot.playing && !waiting && timeline.release(playback.time)) {
        apply(playback.time);
        return;
      }
      extend();
    });
  }, [playback, timeline, apply, extend]);

  // 押している間に強さを変えたら、その時刻で区切って計算し直す
  const setAcceleration = useCallback(
    (acceleration: number) => {
      accelerationRef.current = acceleration;
      if (playback && timeline.setAcceleration(playback.time, acceleration)) apply(playback.time);
    },
    [playback, timeline, apply],
  );

  const press = useCallback(() => {
    if (!playback?.getSnapshot().playing) return;
    if (timeline.press(playback.time, accelerationRef.current)) apply(playback.time);
  }, [playback, timeline, apply]);

  const release = useCallback(() => {
    if (playback && timeline.release(playback.time)) apply(playback.time);
  }, [playback, timeline, apply]);

  const boost = useCallback(() => {
    if (!playback?.getSnapshot().playing) return;
    timeline.boost(playback.time);
    apply(playback.time);
  }, [playback, timeline, apply]);

  const isBoosting = useCallback((time: number) => timeline.isBoosting(time), [timeline]);

  const current = view.ride === ride && view.playback === playback && ride !== undefined;
  return {
    controlled: current ? view.controlled : undefined,
    holding: current && view.holding,
    count: current ? view.count : 0,
    press,
    release,
    boost,
    setAcceleration,
    isBoosting,
  };
}
